import { LitElement, html, css } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

import { authenticatedAPICall } from '../../client/api-client';
import {
  twoDigitNumber,
  PickupOrderStatusToBeRemoved,
  PickupOrderStatusBeingRemoved,
  PickupOrderStatusRemoved,
} from '../../util/util';
import '../../elements/pickup-order/pickup-order';
import { showNotification } from '../../LanghoutBetonApp';
import { PickupOrder, getOrderIDs } from '../../types/types';

type PickupOrderList = PickupOrder[];

@customElement('langhout-beton-order-cleanup')
export class LanghoutBetonOrderCleanup extends LitElement {
  static styles = css`
    :host([disableButtons]) {
      pointer-events: none;
      opacity: 0.7;
    }

    h2 {
      color: red;
    }
  `;

  private limit = 100;
  private offset = 0;

  @property({ type: Boolean, reflect: true })
  private disableButtons = false;

  @property({ type: Array })
  private pickupOrders: PickupOrderList = [];

  constructor() {
    super();
    this.listPickupOrders();
    document.addEventListener(
      'refresh-tasks',
      this.refreshTaskListener as EventListener,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(
      'refresh-tasks',
      this.refreshTaskListener as EventListener,
    );
  }

  refreshTaskListener = () => {
    this.listPickupOrders();
  };

  render() {
    if (this.pickupOrders.length === 0) {
      return html``;
    }
    return html`
      <h2>Pickuporders vervallen</h2>
      ${repeat(
        this.pickupOrders,
        pickupOrder => pickupOrder.id,
        pickupOrder => {
          return html`
            <pickup-order
              .pickupOrder="${pickupOrder}"
              cleanup
              @button-clicked=${async () => {
                this.disableButtons = true;
                if (pickupOrder.status === PickupOrderStatusBeingRemoved) {
                  showNotification(
                    `Order: ${getOrderIDs(pickupOrder)} opgeruimd`,
                  );
                  await this.taskDone(pickupOrder);
                } else {
                  await this.pickUpTask(pickupOrder);
                }
                this.disableButtons = false;
              }}
            ></pickup-order>
          `;
        },
      )}
    `;
  }

  taskDone = async (pickupOrder: PickupOrder) => {
    const path = `/pickuporders/${pickupOrder.id}/status`;
    const body = {
      status: PickupOrderStatusRemoved,
    };
    await authenticatedAPICall(
      path,
      'PUT',
      new Headers(),
      JSON.stringify(body),
    );
    this.listPickupOrders();
  };

  pickUpTask = async (pickupOrder: PickupOrder) => {
    const path = `/pickuporders/${pickupOrder.id}/status`;
    const body = {
      status: PickupOrderStatusBeingRemoved,
    };
    await authenticatedAPICall(
      path,
      'PUT',
      new Headers(),
      JSON.stringify(body),
    );
    this.listPickupOrders();
  };

  listPickupOrders = async () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    const path = `/pickuporders/tasks?offset=${this.offset}&limit=${this.limit}&states=${PickupOrderStatusToBeRemoved},${PickupOrderStatusBeingRemoved}`;
    const pickupOrdersResponse = await authenticatedAPICall(
      path,
      'GET',
      new Headers(),
    );
    if (pickupOrdersResponse.statusCode !== undefined && pickupOrdersResponse.statusCode >= 200 && pickupOrdersResponse.statusCode < 300 && Array.isArray(pickupOrdersResponse.body)) {
      this.pickupOrders = pickupOrdersResponse.body as PickupOrderList;
    }
  };

  formatDate = (date: Date): string => {
    return (
      twoDigitNumber(date.getDate()) +
      '-' +
      twoDigitNumber(date.getMonth() + 1) +
      '-' +
      date.getFullYear()
    );
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'langhout-beton-order-cleanup': LanghoutBetonOrderCleanup;
  }
}
